/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import '../types/moengage';

/** Records the last time the user dismissed our pre-prompt, as an epoch ms string. */
const SNOOZE_STORAGE_KEY = 'move-web-push-opt-in-snoozed-at';

const DEFAULT_DELAY_MS = 5000;
const DEFAULT_SNOOZE_DAYS = 30;
const DEFAULT_SDK_TIMEOUT_MS = 15000;
const SDK_POLL_INTERVAL_MS = 500;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface UseWebPushOptInOptions {
  /** Set false to keep the pre-prompt off entirely (kill switch / per-page opt-out). */
  enabled?: boolean;
  /** How long to wait after mount before showing the pre-prompt. */
  delayMs?: number;
  /** How long a dismissal suppresses the pre-prompt for. */
  snoozeDays?: number;
  /** How long to wait for GTM to inject the MoEngage SDK before giving up. */
  sdkTimeoutMs?: number;
  onError?: (error: Error) => void;
}

export interface UseWebPushOptInResult {
  /** True once every precondition is met and the pre-prompt should be rendered. */
  visible: boolean;
  /** Hands off to the native browser prompt. Must be called from a user gesture. */
  allow: () => void;
  /** Hides the pre-prompt and snoozes it for `snoozeDays`. */
  dismiss: () => void;
}

const readSnoozedAt = (): number | null => {
  try {
    const raw = window.localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Safari private mode and hardened privacy settings throw on access.
    return null;
  }
};

const writeSnoozedAt = (value: number) => {
  try {
    window.localStorage.setItem(SNOOZE_STORAGE_KEY, String(value));
  } catch {
    // Losing the snooze is preferable to breaking the click handler.
  }
};

/**
 * Drives our own web push pre-prompt ("soft ask") ahead of the browser's
 * permission dialog.
 *
 * The MoEngage workspace is configured for **self handled opt-in**, so the SDK
 * renders nothing on its own — we decide when to ask, and `call_web_push()`
 * takes over from there. We intentionally do not use the SDK's `soft_ask`
 * config: it binds to `main_class` / `allow_class` / `block_class` by querying
 * the DOM, which react-native-web gives us no stable way to provide.
 */
export const useWebPushOptIn = ({
  enabled = true,
  delayMs = DEFAULT_DELAY_MS,
  snoozeDays = DEFAULT_SNOOZE_DAYS,
  sdkTimeoutMs = DEFAULT_SDK_TIMEOUT_MS,
  onError,
}: UseWebPushOptInOptions = {}): UseWebPushOptInResult => {
  const [visible, setVisible] = useState(false);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!enabled) return;
    // SSR, and browsers without the push primitives (notably iOS Safari when
    // the site is not installed to the home screen).
    if (typeof window === 'undefined') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    // 'granted' means we are already subscribed; 'denied' is a hard block that
    // no amount of asking can reverse. Only 'default' is still winnable.
    if (window.Notification.permission !== 'default') return;

    const snoozedAt = readSnoozedAt();
    if (
      snoozedAt !== null &&
      Date.now() - snoozedAt < snoozeDays * MS_PER_DAY
    ) {
      return;
    }

    let showTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let elapsed = 0;

    // GTM loads on window `load` and injects the SDK afterwards, so there is no
    // event to await — poll until it shows up or we give up.
    pollTimer = setInterval(() => {
      elapsed += SDK_POLL_INTERVAL_MS;

      if (window.Moengage) {
        clearInterval(pollTimer);
        showTimer = setTimeout(() => setVisible(true), delayMs);
        return;
      }

      if (elapsed >= sdkTimeoutMs) {
        clearInterval(pollTimer);
        onErrorRef.current?.(
          new Error(
            'MoEngage SDK was not injected by GTM before the timeout; web push pre-prompt suppressed',
          ),
        );
      }
    }, SDK_POLL_INTERVAL_MS);

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      if (showTimer) clearTimeout(showTimer);
    };
  }, [enabled, delayMs, snoozeDays, sdkTimeoutMs]);

  const allow = useCallback(() => {
    setVisible(false);
    // Snooze on hand-off too. If the user closes the native prompt without
    // choosing, permission stays 'default' and we would otherwise re-ask on
    // every page load — which is exactly what browsers penalise.
    writeSnoozedAt(Date.now());
    try {
      // Deliberately synchronous: Chrome and Safari only honour
      // Notification.requestPermission() while the user gesture is still
      // active, so nothing may be awaited between the click and this call.
      window.Moengage?.call_web_push();
    } catch (error) {
      onErrorRef.current?.(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    writeSnoozedAt(Date.now());
  }, []);

  return { visible, allow, dismiss };
};

export default useWebPushOptIn;
